/* Phase 10 — MediBooks theme provider.
 *
 * Applies per-hospital theme by rewriting a handful of --mb-* CSS variables
 * on <html>. Tailwind + all colors flow from those variables (see
 * tailwind.config.js + index.css). Consumers get:
 *   - useTheme() → { activeTheme, applyTheme, loadHospitalTheme,
 *                    loadOwnHospitalTheme, resetToPlatformDefault, primaryHex, accentHex }
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";

// -------------- platform defaults (mirror index.css) --------------
export const PLATFORM_DEFAULT_THEME = {
    mode: "dark",
    primary_color: "#14b8a6",
    secondary_color: "#0f766e",
    accent_color: "#14b8a6",
    background_color: "#0a0a0a",
    surface_color: "#141414",   // matches --mb-surface2 (input/hover); public API uses this
    border_color: "#262626",
    text_primary_color: "#ffffff",
    text_secondary_color: "#a3a3a3",
    logo_url: null,
    favicon_url: null,
    brand_name: null,
    is_active: false,
    super_admin_override_disabled: false,
};

const KEY_TO_VAR = {
    primary_color: "--mb-primary",
    secondary_color: "--mb-secondary",
    accent_color: "--mb-accent",
    background_color: "--mb-bg",
    surface_color: "--mb-surface2",  // primary surface for inputs / hover
    border_color: "--mb-border",
    text_primary_color: "--mb-fg",
    text_secondary_color: "--mb-fg2",
};

function hexToRgbTriple(hex) {
    if (!hex || typeof hex !== "string") return null;
    const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
    if (!m) return null;
    const n = parseInt(m[1], 16);
    return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

function shade(hex, factor) {
    // factor > 1 lightens, factor < 1 darkens (we mostly darken for surface).
    if (!hex) return null;
    const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
    if (!m) return null;
    const n = parseInt(m[1], 16);
    const r = Math.max(0, Math.min(255, Math.round(((n >> 16) & 255) * factor)));
    const g = Math.max(0, Math.min(255, Math.round(((n >> 8) & 255) * factor)));
    const b = Math.max(0, Math.min(255, Math.round((n & 255) * factor)));
    return `${r} ${g} ${b}`;
}

const ThemeContext = createContext(null);

let _originalFavicon = null;
function ensureOriginalFavicon() {
    if (_originalFavicon !== null) return;
    const link = document.querySelector('link[rel="icon"]');
    _originalFavicon = link ? link.getAttribute("href") : "";
}

function setFavicon(href) {
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "icon");
        document.head.appendChild(link);
    }
    link.setAttribute("href", href || _originalFavicon || "");
}

export function ThemeProvider({ children }) {
    const [activeTheme, setActiveTheme] = useState(PLATFORM_DEFAULT_THEME);
    const cacheRef = useRef(new Map());   // hospital_id -> theme
    const inflightRef = useRef(0);        // race guard: latest request wins

    const applyTheme = useCallback((theme) => {
        ensureOriginalFavicon();
        const merged = { ...PLATFORM_DEFAULT_THEME, ...(theme || {}) };
        const root = document.documentElement;

        for (const [key, cssVar] of Object.entries(KEY_TO_VAR)) {
            const triple = hexToRgbTriple(merged[key]);
            if (triple) root.style.setProperty(cssVar, triple);
        }
        // Derive surface (cards) as slightly lighter than bg, and surface2 override kept from surface_color.
        const bgShade = shade(merged.background_color, 1.2);
        if (bgShade) root.style.setProperty("--mb-surface", bgShade);

        // data-theme for CSS hooks
        root.setAttribute("data-theme", merged.mode || "dark");

        // Favicon (only when explicit)
        if (merged.favicon_url) setFavicon(merged.favicon_url);
        else setFavicon(_originalFavicon || "");

        setActiveTheme(merged);
    }, []);

    const resetToPlatformDefault = useCallback(() => {
        applyTheme(PLATFORM_DEFAULT_THEME);
    }, [applyTheme]);

    const loadHospitalTheme = useCallback(async (hospitalIdOrSlug) => {
        if (!hospitalIdOrSlug) {
            resetToPlatformDefault();
            return;
        }
        if (cacheRef.current.has(hospitalIdOrSlug)) {
            applyTheme(cacheRef.current.get(hospitalIdOrSlug));
            return;
        }
        const myTicket = ++inflightRef.current;
        try {
            const { data } = await api.get(`/public/hospitals/${hospitalIdOrSlug}/theme`);
            cacheRef.current.set(hospitalIdOrSlug, data);
            if (myTicket === inflightRef.current) applyTheme(data);
        } catch {
            if (myTicket === inflightRef.current) resetToPlatformDefault();
        }
    }, [applyTheme, resetToPlatformDefault]);

    const loadOwnHospitalTheme = useCallback(async () => {
        const myTicket = ++inflightRef.current;
        try {
            const { data } = await api.get("/hospital/theme");
            if (myTicket === inflightRef.current) applyTheme(data);
        } catch {
            if (myTicket === inflightRef.current) resetToPlatformDefault();
        }
    }, [applyTheme, resetToPlatformDefault]);

    // Apply platform default immediately on mount (already the css-var default,
    // this just syncs React state).
    useEffect(() => {
        ensureOriginalFavicon();
    }, []);

    const value = useMemo(() => ({
        activeTheme,
        applyTheme,
        loadHospitalTheme,
        loadOwnHospitalTheme,
        resetToPlatformDefault,
        primaryHex: activeTheme.primary_color,
        accentHex: activeTheme.accent_color,
        secondaryHex: activeTheme.secondary_color,
    }), [activeTheme, applyTheme, loadHospitalTheme, loadOwnHospitalTheme, resetToPlatformDefault]);

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
    return ctx;
}
